import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  TextField,
  Alert,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ModalFooter from "@/modules/common/components/modals/modal-footer";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { useMutation } from "@tanstack/react-query";
import { createOrganizationTarget } from "@/modules/targets/api/index.v2";
import { getCurrentQuarter } from "@/modules/targets/utils";
import { formatCurrency, parseNumber, showToast } from "@/modules/common/utils";
import {
  ToastType,
  TOAST_IDS,
  EnvironmentVariables,
  OrganizationTierTypes,
} from "@/modules/common/types";
import { UUID } from "crypto";
import { useParams } from "react-router-dom";
import { targetTypeOptions } from "@/modules/targets/constants";
import { SyntheticEvent, useState } from "react";
import { TargetTypes } from "@/modules/targets/types";
import { queryClient } from "@/queryClient";
import { useTargetsTemplateContext } from "@/modules/targets/context/targets-template.context";

interface Organization {
  id: string;
  label: string;
}

interface FormInputs {
  organizations: Organization[];
  agentTargets: {
    [key: string]: string; // Dynamic month keys
  };
  leaderTargets: {
    [key: string]: string; // Dynamic month keys
  };
}

interface CreateAgentTargetProps {
  organizationSubDepartment: string;
  onClose: () => void;
}

const calculateTotal = (targets: { [key: string]: string }): number => {
  if (!targets) return 0;
  return Object.values(targets).reduce((total, value) => {
    return total + parseNumber(value);
  }, 0);
};

const validateMonthValue = (value: string) => {
  const numValue = parseNumber(value);
  if (!value) return "This field is required";
  if (numValue <= 0) return "Value must be greater than 0";
  return true;
};

const CreateOrganizationTarget = ({
  organizationSubDepartment,
  onClose,
}: CreateAgentTargetProps) => {
  const currentQuarter = getCurrentQuarter();
  const quarterMonths = [
    currentQuarter.m1,
    currentQuarter.m2,
    currentQuarter.m3,
  ];

  const { uid } = useParams();

  const { subDepartment } = useTargetsTemplateContext();

  // Create default values dynamically based on current quarter
  const createDefaultTargets = () => {
    const defaults: { [key: string]: string } = {};
    quarterMonths.forEach((month) => {
      defaults[month.toLowerCase()] = "";
    });
    return defaults;
  };

  const { control, handleSubmit, watch } = useForm<FormInputs>({
    defaultValues: {
      agentTargets: createDefaultTargets(),
      leaderTargets: createDefaultTargets(),
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: createOrganizationTarget,
    onSuccess: () => {
      showToast({
        type: ToastType.SUCCESS,
        message: "Organization target created successfully",
        toastId: TOAST_IDS.CREATE_ORGANIZATION_TARGET_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      queryClient.invalidateQueries([
        `organizationTargets-${currentQuarter.startDate}-${currentQuarter.endDate}-${uid}`,
      ]);
      onClose?.();
    },
    onError: () => {
      showToast({
        type: ToastType.ERROR,
        message: "Organization target creation failed",
        toastId: TOAST_IDS.CREATE_ORGANIZATION_TARGET_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  // Watch target fields dynamically
  const monthTargets = quarterMonths.map((month) => ({
    month: month.toLowerCase(),
    agentTarget: watch(`agentTargets.${month.toLowerCase()}`),
    leaderTarget: watch(`leaderTargets.${month.toLowerCase()}`),
  }));

  // Calculate totals
  const organizationTotal = calculateTotal(
    monthTargets.reduce(
      (acc, { month, agentTarget }) => ({
        ...acc,
        [month]: agentTarget,
      }),
      {}
    )
  );

  const [targetType, setTargetType] = useState<TargetTypes>(TargetTypes.GMV);

  const handleTargetTypeChange = (
    _: SyntheticEvent<Element, Event>,
    value: { label: string; value: TargetTypes } | null
  ) => {
    if (value) {
      setTargetType(value.value);
    } else {
      setTargetType(targetTypeOptions[0].value);
    }
  };

  const onSubmit = (data: FormInputs) => {
    const startMonth = currentQuarter.quarterNumber * 3 - 3;

    const targetPeriods = quarterMonths
      .map((month, index) => {
        const monthIndex = startMonth + index;
        return {
          from: new Date(Date.UTC(currentQuarter.year, monthIndex, 1, 0, 0, 0)),
          to: new Date(
            Date.UTC(currentQuarter.year, monthIndex + 1, 0, 0, 0, 0)
          ),
          amount: parseNumber(data.agentTargets[month.toLowerCase()]),
        };
      })
      .filter((period) => period.amount > 0);

    const payload = {
      subDepartmentUId: subDepartment?.uid,
      organizationUIds: [uid as UUID],
      targetType,
      targetPeriods,
    };

    mutate(payload);
  };

  const { isMobile } = useScreenSize();

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : 800,
        bgcolor: "background.white",
        borderRadius: "8px",
        p: isMobile ? 2 : 3,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant={"h5_bold"} sx={{ color: "neutral.900", mb: 2 }}>
            {organizationSubDepartment} Organization Target -{" "}
            {currentQuarter.quarterLabel}
          </Typography>

          {EnvironmentVariables.COMPANY_NAME.toString() ===
            OrganizationTierTypes.NAWY && (
            <Autocomplete
              sx={{ width: isMobile ? "100%" : "30%" }}
              defaultValue={targetTypeOptions[0]}
              value={targetTypeOptions.find(
                (option) => option.value === targetType
              )}
              size="small"
              options={targetTypeOptions}
              getOptionLabel={(option) => option.label}
              onChange={handleTargetTypeChange}
              renderInput={(params) => (
                <TextField {...params} label="Target Type" />
              )}
            />
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              mb: 4,
            }}
          >
            {quarterMonths.map((month) => (
              <Controller
                key={month}
                name={`agentTargets.${month.toLowerCase()}`}
                control={control}
                rules={{ validate: validateMonthValue }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label={month}
                    size="small"
                    sx={{ flex: 1 }}
                    onChange={(e) => {
                      const formattedValue = formatCurrency(e.target.value);
                      field.onChange(formattedValue);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">EGP</InputAdornment>
                      ),
                    }}
                    error={!!error}
                    helperText={error?.message}
                    disabled={isLoading}
                  />
                )}
              />
            ))}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "row" : "column",
                justifyContent: "space-between",
                alignItems: isMobile ? "center" : "flex-start",
                minWidth: isMobile ? "100%" : 120,
                mt: isMobile ? 2 : 0,
              }}
            >
              <Typography variant="body_md_reg" sx={{ color: "neutral.500" }}>
                Total
              </Typography>
              <Typography variant={"h5_bold"} sx={{ color: "neutral.900" }}>
                {organizationTotal.toLocaleString()} EGP
              </Typography>
            </Box>
          </Box>
        </Box>

        <Alert
          icon={<InfoIcon />}
          severity="warning"
          sx={{
            mb: 4,
            color: "warning.600",
            bgcolor: "warning.50",
            "& .MuiAlert-icon": {
              color: "warning.600",
            },
          }}
        >
          Please note that editing targets after creation is not allowed
        </Alert>

        <ModalFooter
          handleClose={onClose}
          handleConfirm={handleSubmit(onSubmit)}
          confirmText={"Create"}
          cancelText="Cancel"
          isSubmitting={isLoading}
          isForm={true}
          disabledConfirmButton={isLoading}
        />
      </form>
    </Box>
  );
};

export default CreateOrganizationTarget;
